import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, X, Trash2, Eye, Heart, MessageCircle, Bookmark, Share2, ExternalLink, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const Posts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/posts").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = filter === "all" ? items : items.filter((p) => p.platform === filter);

  return (
    <Layout
      title="Publicaciones & Métricas"
      subtitle="Registro manual y análisis"
      actions={
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-post-btn">
          <Plus size={14} /> Registrar publicación
        </button>
      }
    >
      <div className="flex gap-2 mb-5 flex-wrap">
        {["all", "instagram", "tiktok", "facebook", "youtube_shorts", "whatsapp"].map((p) => (
          <button key={p} onClick={() => setFilter(p)} className={`pill cursor-pointer ${filter === p ? "pill-yellow" : "pill-gray"}`} data-testid={`filter-${p}`}>
            {p === "all" ? "Todas" : p}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="aura-card-flat p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
            <BarChart3 size={24} />
          </div>
          <h3 className="font-display text-2xl font-medium">Sin publicaciones registradas</h3>
          <p className="text-[#666] mt-2 mb-5">Registra manualmente cada post + sus métricas para que Aura aprenda.</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">Registrar primera publicación</button>
        </div>
      ) : (
        <div className="aura-card-flat p-2 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E5E5E5] text-left text-xs uppercase tracking-wider text-[#666]">
                <th className="p-3">Título</th>
                <th className="p-3">Canal</th>
                <th className="p-3">Creador</th>
                <th className="p-3">Vistas</th>
                <th className="p-3">Likes</th>
                <th className="p-3">Coment.</th>
                <th className="p-3">Guard.</th>
                <th className="p-3">Comp.</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody data-testid="posts-table">
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`post-${p.id}`}>
                  <td className="p-3 text-sm">
                    <div className="font-medium">{p.title}</div>
                    <div className="text-xs text-[#666]">{p.format} · {p.campaign_name || "sin campaña"}</div>
                  </td>
                  <td className="p-3 text-sm">{p.platform}</td>
                  <td className="p-3 text-sm">{p.creator_name || "—"}</td>
                  <td className="p-3 text-sm font-medium">{(p.views || 0).toLocaleString()}</td>
                  <td className="p-3 text-sm">{(p.likes || 0).toLocaleString()}</td>
                  <td className="p-3 text-sm">{p.comments || 0}</td>
                  <td className="p-3 text-sm">{p.saves || 0}</td>
                  <td className="p-3 text-sm">{p.shares || 0}</td>
                  <td className="p-3 text-sm">
                    <div className="flex gap-2">
                      {p.post_link && <a href={p.post_link} target="_blank" rel="noopener noreferrer" className="text-[#8a5e10]"><ExternalLink size={13} /></a>}
                      <button onClick={async () => {
                        if (!window.confirm("¿Eliminar?")) return;
                        await api.delete(`/posts/${p.id}`);
                        load();
                      }} className="text-[#666] hover:text-red-600"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <PostForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </Layout>
  );
};

const PostForm = ({ onClose, onSaved }) => {
  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => { api.get("/campaigns").then((r) => setCampaigns(r.data)); }, []);
  const [form, setForm] = useState({
    title: "", platform: "instagram", account: "@rubiperfumeria", creator_name: "",
    format: "post", campaign_id: "", branch: "", post_link: "", published_at: new Date().toISOString().split("T")[0],
    views: 0, reach: 0, likes: 0, comments: 0, shares: 0, saves: 0, clicks: 0,
    messages: 0, leads: 0, sales: 0, observations: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const camp = campaigns.find((c) => c.id === form.campaign_id);
      const body = {
        ...form,
        campaign_name: camp?.name || null,
        views: parseInt(form.views) || 0,
        reach: parseInt(form.reach) || 0,
        likes: parseInt(form.likes) || 0,
        comments: parseInt(form.comments) || 0,
        shares: parseInt(form.shares) || 0,
        saves: parseInt(form.saves) || 0,
        clicks: parseInt(form.clicks) || 0,
        messages: parseInt(form.messages) || 0,
        leads: parseInt(form.leads) || 0,
        sales: parseInt(form.sales) || 0,
      };
      // Compute rates
      if (body.reach > 0) {
        body.interaction_rate = ((body.likes + body.comments + body.shares + body.saves) / body.reach) * 100;
        body.saves_rate = (body.saves / body.reach) * 100;
        body.comment_rate = (body.comments / body.reach) * 100;
        body.virality_rate = (body.shares / body.reach) * 100;
      }
      await api.post("/posts", body);
      toast.success("Publicación registrada");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-7 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <div className="label-overline">Registrar</div>
            <h2 className="font-display text-2xl font-medium">Nueva publicación</h2>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="aura-label">Título *</label><input required className="aura-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-title" /></div>
          <div><label className="aura-label">Plataforma</label><select className="aura-input" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>{["instagram", "tiktok", "facebook", "youtube_shorts", "whatsapp", "tiktok_shop"].map((p) => <option key={p}>{p}</option>)}</select></div>
          <div><label className="aura-label">Cuenta</label><input className="aura-input" value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })} /></div>
          <div><label className="aura-label">Creador</label><input className="aura-input" value={form.creator_name} onChange={(e) => setForm({ ...form, creator_name: e.target.value })} /></div>
          <div><label className="aura-label">Formato</label><select className="aura-input" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}>{["post", "reel", "story", "carrusel", "video", "live"].map((f) => <option key={f}>{f}</option>)}</select></div>
          <div><label className="aura-label">Campaña</label><select className="aura-input" value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}><option value="">— —</option>{campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
          <div><label className="aura-label">Sucursal</label><input className="aura-input" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} /></div>
          <div className="col-span-2"><label className="aura-label">Link publicado</label><input className="aura-input" value={form.post_link} onChange={(e) => setForm({ ...form, post_link: e.target.value })} placeholder="https://..." /></div>
          <div><label className="aura-label">Fecha publicación</label><input type="date" className="aura-input" value={form.published_at?.split?.("T")?.[0] || form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })} /></div>
        </div>

        <div className="border-t border-[#E5E5E5] pt-4">
          <div className="label-overline mb-3">Métricas</div>
          <div className="grid grid-cols-3 gap-3">
            {[
              ["views", "Vistas"], ["reach", "Alcance"], ["likes", "Likes"],
              ["comments", "Comentarios"], ["shares", "Compartidos"], ["saves", "Guardados"],
              ["clicks", "Clics"], ["messages", "Mensajes"], ["leads", "Leads"],
            ].map(([k, l]) => (
              <div key={k}>
                <label className="aura-label">{l}</label>
                <input type="number" min="0" className="aura-input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`metric-${k}`} />
              </div>
            ))}
            <div>
              <label className="aura-label">Ventas atribuidas</label>
              <input type="number" min="0" className="aura-input" value={form.sales} onChange={(e) => setForm({ ...form, sales: e.target.value })} />
            </div>
          </div>
        </div>

        <div>
          <label className="aura-label">Observaciones</label>
          <textarea className="aura-input" rows="2" value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} placeholder="Por qué crees que funcionó / no funcionó..." />
        </div>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
          <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center flex items-center gap-2" data-testid="post-submit">
            {saving && <span className="spinner" />} Guardar publicación
          </button>
        </div>
      </form>
    </div>
  );
};

export default Posts;
